import type { ReactElement } from 'react';
import { useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    ChevronDown,
    ChevronRight,
    GripVertical,
    MoreVertical,
    Pencil,
    Plus,
    Trash2
} from 'lucide-react';

import type { SpecializationTreeNode } from '@/lib/buildSpecializationTree';

interface SpecializationNodeProps {
    node: SpecializationTreeNode;
    /** Уровень в дереве для расчёта отступа. depth из самой ноды используется
     * как fallback, если уровень не передан явно (для случаев вне dnd-контекста). */
    level?: number;
    /** Раскрыт ли узел (управляется родителем для удобства "развернуть все"). */
    expanded: boolean;
    onToggleExpanded: (id: string) => void;
    onEdit: (node: SpecializationTreeNode) => void;
    onDelete: (node: SpecializationTreeNode) => void;
    onAddChild: (node: SpecializationTreeNode) => void;
    /** Включён ли режим drag-and-drop (вне scope multi-select-view = false). */
    draggable?: boolean;
    /** Multi-select view — чекбоксы вместо drag-handle и меню. */
    selectMode?: boolean;
    selected?: boolean;
    onToggleSelected?: (id: string) => void;
    children?: ReactElement | ReactElement[];
}

export function SpecializationNode({
    node,
    level,
    expanded,
    onToggleExpanded,
    onEdit,
    onDelete,
    onAddChild,
    draggable = true,
    selectMode = false,
    selected = false,
    onToggleSelected,
    children
}: SpecializationNodeProps): ReactElement {
    const [menuOpen, setMenuOpen] = useState(false);

    const sortable = useSortable({ id: node.id, disabled: !draggable });
    const droppable = useDroppable({
        id: `drop-onto-${node.id}`,
        data: { type: 'drop-onto', nodeId: node.id },
        disabled: !draggable
    });

    const style = {
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
        opacity: sortable.isDragging ? 0.4 : 1
    };

    const depthLevel = level ?? node.depth;
    const paddingLeft = `${depthLevel * 24}px`;

    // Стилизация по уровню согласно спецификации
    let nameClass = 'text-sm text-gray-700';
    if (depthLevel === 0) {
        nameClass = 'text-base font-semibold text-gray-900';
    } else if (node.children.length > 0) {
        nameClass = 'text-sm font-medium text-gray-800';
    }
    if (!node.isActive) {
        nameClass = 'text-sm italic text-gray-400';
    }

    const hasChildren = node.children.length > 0;
    const isRoot = node.parentId === null;
    const canPublishBadge = isRoot && node.canPublishRecipes === true;
    const cannotPublishBadge = isRoot && node.canPublishRecipes === false;

    const dropOntoClass = droppable.isOver
        ? 'bg-amber-50 border-amber-300 border-dashed'
        : 'border-transparent';

    const handleRowClick = (): void => {
        if (selectMode && onToggleSelected) {
            onToggleSelected(node.id);
        }
    };

    return (
        <div ref={sortable.setNodeRef} style={style}>
            <div
                ref={droppable.setNodeRef}
                className={`flex items-center gap-2 border ${dropOntoClass} rounded px-1 py-1.5 transition hover:bg-gray-50`}
                style={{ paddingLeft }}
                data-testid={`specialization-node-${node.id}`}
                onClick={handleRowClick}
                role={selectMode ? 'button' : undefined}
            >
                {!selectMode && draggable && (
                    <button
                        type="button"
                        {...sortable.attributes}
                        {...sortable.listeners}
                        aria-label="Перетащить"
                        className="flex cursor-grab items-center text-gray-400 transition hover:text-gray-700 active:cursor-grabbing"
                        data-testid={`specialization-drag-handle-${node.id}`}
                    >
                        <GripVertical size={16} />
                    </button>
                )}

                {selectMode && (
                    <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => onToggleSelected?.(node.id)}
                        data-testid={`specialization-checkbox-${node.id}`}
                        className="h-4 w-4 cursor-pointer"
                        onClick={(e) => e.stopPropagation()}
                    />
                )}

                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggleExpanded(node.id);
                    }}
                    aria-label={expanded ? 'Свернуть' : 'Развернуть'}
                    className={`flex items-center text-gray-500 ${hasChildren ? 'hover:text-gray-900' : 'invisible'}`}
                    data-testid={`specialization-expand-${node.id}`}
                >
                    {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>

                <span className={`flex-1 truncate ${nameClass}`}>
                    {node.name}
                    {canPublishBadge && (
                        <span
                            className="ml-2 inline-block rounded bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-800"
                            data-testid={`specialization-publish-badge-${node.id}`}
                        >
                            публикация
                        </span>
                    )}
                    {cannotPublishBadge && (
                        <span
                            className="ml-2 inline-block rounded bg-gray-200 px-1.5 py-0.5 text-xs font-medium text-gray-700"
                            data-testid={`specialization-no-publish-badge-${node.id}`}
                        >
                            без публикации
                        </span>
                    )}
                    {!node.isActive && (
                        <span
                            className="ml-2 text-xs italic text-gray-400"
                            data-testid={`specialization-archived-badge-${node.id}`}
                        >
                            (архив)
                        </span>
                    )}
                    <span className="ml-2 text-xs text-gray-400">
                        /{node.slug}
                        {node.usageCount > 0 && ` · ${node.usageCount}`}
                    </span>
                </span>

                {!selectMode && (
                    <div className="relative">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                setMenuOpen((v) => !v);
                            }}
                            aria-label="Меню действий"
                            className="rounded p-1 text-gray-500 transition hover:bg-gray-100"
                            data-testid={`specialization-menu-${node.id}`}
                        >
                            <MoreVertical size={16} />
                        </button>
                        {menuOpen && (
                            <>
                                <button
                                    type="button"
                                    onClick={() => setMenuOpen(false)}
                                    aria-label="Закрыть меню"
                                    className="fixed inset-0 z-10 cursor-default"
                                />
                                <div
                                    className="absolute right-0 top-full z-20 mt-1 w-44 overflow-hidden rounded border border-gray-200 bg-white shadow-lg"
                                    data-testid={`specialization-menu-popover-${node.id}`}
                                >
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setMenuOpen(false);
                                            onEdit(node);
                                        }}
                                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 transition hover:bg-gray-100"
                                        data-testid={`specialization-menu-edit-${node.id}`}
                                    >
                                        <Pencil size={14} /> Редактировать
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setMenuOpen(false);
                                            onAddChild(node);
                                        }}
                                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 transition hover:bg-gray-100"
                                        data-testid={`specialization-menu-add-child-${node.id}`}
                                    >
                                        <Plus size={14} /> Добавить дочерний
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setMenuOpen(false);
                                            onDelete(node);
                                        }}
                                        className="flex w-full items-center gap-2 border-t border-gray-100 px-3 py-2 text-left text-sm text-red-600 transition hover:bg-red-50"
                                        data-testid={`specialization-menu-delete-${node.id}`}
                                    >
                                        <Trash2 size={14} /> Удалить
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            {expanded && children !== undefined && (
                <div data-testid={`specialization-children-${node.id}`}>{children}</div>
            )}
        </div>
    );
}
